/* eslint-disable react-hooks/exhaustive-deps */
import {BaseButton, SecondaryButton} from "components"
import {BaseLoading} from "components/Loading"
import {Screen} from "components/ui"
import {checkPermission, formatPhoneNumber, handleError, toastError, toastSuccess} from "helpers"
import {routing} from "helpers/routing"
import {useModel, useMutation} from "hooks"
import React, {useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import {useQuery} from "@tanstack/react-query"
import {useHistory, useParams} from "react-router-dom"
import axios from "axios"
import {fileService, smsService} from "services"
import {Permissions} from "types/permission"
import {
    createEmptyDialerSettings,
    SmsPhoneNumberDialerDetailResponse,
    SmsPhoneNumberDialerSettings,
    VoicemailVoice
} from "types/sms-dialer-settings"
import SmsPhoneNumberGeneralInfo from "./SmsPhoneNumberGeneralInfo"
import DialerRoutingForm from "./DialerRoutingForm"
import styles from "./PhoneNumberDetails.module.css"
import {Divider} from "antd"
import cx from "classnames"

type Params = {
    smsPhoneNumbersId: string
}

type VoicemailSource = "generate" | "upload"

type VoicemailSnapshot = {
    script: string
    voice: VoicemailVoice
}

const getVoicemailSnapshot = (settings: SmsPhoneNumberDialerSettings): VoicemailSnapshot => ({
    script: settings.voicemailScript?.trim() ?? "",
    voice: settings.voicemailVoice
})

const isVoicemailOutOfSync = (settings: SmsPhoneNumberDialerSettings, snapshot: VoicemailSnapshot) => {
    const current = getVoicemailSnapshot(settings)
    return current.script !== snapshot.script || current.voice !== snapshot.voice
}

const SmsPhoneNumberDetail: React.FC = () => {
    const {t} = useTranslation(["settings", "common"])
    const model = useModel()
    const history = useHistory()
    const params = useParams<Params>()
    const smsPhoneNumbersId = useMemo(() => {
        const id = parseInt(params.smsPhoneNumbersId, 10)
        return isNaN(id) ? null : id
    }, [params.smsPhoneNumbersId])

    const [phone, setPhone] = useState<SmsPhoneNumberDialerDetailResponse["phone"] | null>(null)
    const [settings, setSettings] = useState<SmsPhoneNumberDialerSettings | null>(null)
    const [generatedVoicemailSnapshot, setGeneratedVoicemailSnapshot] = useState<VoicemailSnapshot | null>(null)
    const [voicemailSource, setVoicemailSource] = useState<VoicemailSource | null>(null)
    const [pendingVoicemailFile, setPendingVoicemailFile] = useState<File | null>(null)
    const [isSubmitDisabled, setSubmitDisabled] = useState(true)
    const [isSubmitting, setSubmitting] = useState(false)

    const canEdit = checkPermission({staff: [Permissions.Staff.Settings.General.Sms.Edit]}, model)

    const {isFetching} = useQuery(
        ["sms-dialer-settings", smsPhoneNumbersId],
        () => smsService.getDialerSettings(smsPhoneNumbersId!),
        {
            enabled: !!smsPhoneNumbersId,
            onSuccess: (data: SmsPhoneNumberDialerDetailResponse) => {
                const loadedSettings = {
                    ...createEmptyDialerSettings(smsPhoneNumbersId!),
                    ...data.settings,
                    routingActions: data.settings?.routingActions ?? [],
                    prioritizeRecentStaff: data.settings?.prioritizeRecentStaff ?? true,
                    voicemailScript: data.settings?.voicemailScript ?? "",
                    voicemailVoice: data.settings?.voicemailVoice ?? "female"
                }
                setPhone(data.phone)
                setSettings(loadedSettings)
                setGeneratedVoicemailSnapshot(
                    data.settings?.voicemailAudioUrl ? getVoicemailSnapshot(loadedSettings) : null
                )
                setVoicemailSource(data.settings?.voicemailAudioUrl ? "generate" : null)
                setPendingVoicemailFile(null)
                setSubmitDisabled(true)
            },
            onError: (e: Error) => {
                handleError(e)
                history.push(routing.settings.sms.index())
            }
        }
    )

    const mutation = useMutation(
        (payload: {
            smsPhoneNumbersId: number
            routingActions: SmsPhoneNumberDialerSettings["routingActions"]
            prioritizeRecentStaff: boolean
            voicemailScript: string | null
            voicemailVoice: SmsPhoneNumberDialerSettings["voicemailVoice"]
            voicemailAudioUrl: string | null
        }) => smsService.updateDialerSettings(payload),
        {
            onSuccess: () => {
                toastSuccess(t("smsSettings.dialerSettings.saveSuccess"))
                setSubmitDisabled(true)
            },
            onError: () => {
                toastError(t("smsSettings.dialerSettings.saveFailure"))
            }
        }
    )

    const buildUpdatePayload = (settingsToSave: SmsPhoneNumberDialerSettings) => ({
        smsPhoneNumbersId: smsPhoneNumbersId!,
        routingActions: settingsToSave.routingActions.map((action, order) => {
            const {staffProfile: _staffProfile, ...rest} = action
            return {...rest, order}
        }),
        prioritizeRecentStaff: Boolean(settingsToSave.prioritizeRecentStaff),
        voicemailScript: settingsToSave.voicemailScript,
        voicemailVoice: settingsToSave.voicemailVoice,
        voicemailAudioUrl: settingsToSave.voicemailAudioUrl ?? null
    })

    const handleVoicemailGenerated = (generatedSettings: SmsPhoneNumberDialerSettings) => {
        setVoicemailSource("generate")
        setPendingVoicemailFile(null)
        setGeneratedVoicemailSnapshot(getVoicemailSnapshot(generatedSettings))
    }

    const handleVoicemailFileSelected = (file: File | null) => {
        setPendingVoicemailFile(file)
        if (file) {
            setVoicemailSource("upload")
            setGeneratedVoicemailSnapshot(null)
        }
    }

    const handleSettingsChange: React.Dispatch<React.SetStateAction<SmsPhoneNumberDialerSettings>> = (value) => {
        setSettings(value)
        setSubmitDisabled(false)
    }

    const handleCancel = () => {
        history.push(routing.settings.sms.index())
    }

    const uploadVoicemailFile = async (file: File) => {
        const fileExt = file.name.split(".").pop()?.toLowerCase()
        if (!fileExt) {
            throw new Error("Invalid audio file")
        }
        const {url, urlForUploading} = await fileService.createSignedUrlForUploadingFile({
            fileExtension: fileExt
        })
        await axios.put(urlForUploading, file, {
            headers: {"Content-Type": file.type || `audio/${fileExt}`}
        })
        return url as string
    }

    const handleSubmit = async () => {
        if (!settings || !smsPhoneNumbersId) return

        const script = settings.voicemailScript?.trim()
        const hasPendingUpload = Boolean(pendingVoicemailFile)
        const hasUploadedAudio =
            voicemailSource === "upload" && (hasPendingUpload || Boolean(settings.voicemailAudioUrl))

        if (!hasUploadedAudio && !script) {
            toastError(t("smsSettings.dialerSettings.requiredScript"))
            return
        }

        let settingsToSave = settings

        if (settings.voicemailAudioUrl && !hasPendingUpload && voicemailSource !== "upload") {
            if (!generatedVoicemailSnapshot || isVoicemailOutOfSync(settings, generatedVoicemailSnapshot)) {
                toastError(t("smsSettings.dialerSettings.generateBeforeSubmit"))
                return
            }
        }

        try {
            setSubmitting(true)

            if (pendingVoicemailFile) {
                const voicemailAudioUrl = await uploadVoicemailFile(pendingVoicemailFile)
                settingsToSave = {
                    ...settings,
                    voicemailAudioUrl,
                    voicemailPlayUrl: voicemailAudioUrl
                }
                setSettings(settingsToSave)
                setPendingVoicemailFile(null)
                setVoicemailSource("upload")
            } else if (!settings.voicemailAudioUrl) {
                if (!script) {
                    toastError(t("smsSettings.dialerSettings.requiredAudio"))
                    return
                }
                const result = await smsService.generateVoicemail({
                    smsPhoneNumbersId,
                    voice: settings.voicemailVoice,
                    script
                })
                if (!result?.settings?.voicemailAudioUrl) {
                    toastError(t("smsSettings.dialerSettings.generateFailure"))
                    return
                }
                settingsToSave = {
                    ...settings,
                    voicemailScript: result.settings.voicemailScript,
                    voicemailVoice: result.settings.voicemailVoice,
                    voicemailAudioUrl: result.settings.voicemailAudioUrl,
                    voicemailPlayUrl: result.settings.voicemailPlayUrl
                }
                setSettings(settingsToSave)
                setVoicemailSource("generate")
            }

            await mutation.mutateAsync(buildUpdatePayload(settingsToSave))
            if (voicemailSource !== "upload" && !hasPendingUpload) {
                setGeneratedVoicemailSnapshot(getVoicemailSnapshot(settingsToSave))
            }
        } catch (error) {
            handleError(error)
            if (pendingVoicemailFile) {
                toastError(t("smsSettings.dialerSettings.uploadFailure"))
            }
        } finally {
            setSubmitting(false)
            history.push(routing.settings.sms.index())
        }
    }

    const title = t("smsSettings.dialerSettings.title")

    return (
        <Screen
            header={{
                title,
                backUrl: routing.settings.sms.index()
            }}
            htmlTitle={title}>
            <BaseLoading isShow={isFetching} />

            {phone && (
                <div className={styles.headerTagsWrap}>
                    <div className={styles.headerTag}>
                        <span className={styles.headerTagLabel}>{t("smsSettings.phoneNumber")}</span>
                        <span className={styles.headerTagValue}>
                            {phone.phone ? formatPhoneNumber(phone.phone) : t("smsSettings.dialerSettings.noValue")}
                        </span>
                    </div>
                    <div className={styles.headerTag}>
                        <span className={styles.headerTagLabel}>{t("smsSettings.areaCode")}</span>
                        <span className={styles.headerTagValue}>
                            {phone.areaCode ?? t("smsSettings.dialerSettings.noValue")}
                        </span>
                    </div>
                    {Boolean(phone.isGlobal) && (
                        <div className={cx(styles.headerTag, styles.headerTagGlobal)}>
                            <span className={styles.headerTagGlobalLabel}>{t("smsSettings.isGlobal")}</span>
                        </div>
                    )}
                </div>
            )}

            {phone && settings && (
                <>
                    <SmsPhoneNumberGeneralInfo
                        phone={phone}
                        settings={settings}
                        onChange={handleSettingsChange}
                        canEdit={canEdit}
                    />
                    <Divider className="border-t-2 border-primary my-40" />
                    <DialerRoutingForm
                        smsPhoneNumbersId={smsPhoneNumbersId!}
                        settings={settings}
                        onChange={handleSettingsChange}
                        onVoicemailGenerated={handleVoicemailGenerated}
                        onVoicemailFileSelected={handleVoicemailFileSelected}
                        canEdit={canEdit}
                    />
                    <div className={styles.detailActions}>
                        <div className="flex-1" />
                        {canEdit && (
                            <>
                                <SecondaryButton
                                    size="large"
                                    title={t("common:action.cancel")}
                                    onClick={handleCancel}
                                />
                                <BaseButton
                                    size="large"
                                    title={t("common:action.save")}
                                    isActive={!isSubmitDisabled}
                                    disabled={isSubmitDisabled}
                                    loading={isSubmitting}
                                    onClick={handleSubmit}
                                />
                            </>
                        )}
                    </div>
                </>
            )}
        </Screen>
    )
}

export default SmsPhoneNumberDetail
